import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Lock, ArrowRight, Copy, Pencil, RefreshCw, Plus, Minus } from 'lucide-react';
import { FIELD_TYPES, SS_STAGES, TT_STAGES, SS_SCHEDULE_CONFIG } from '@/config/stageConfig';
import { calculateWorkingDays, formatWorkingDays } from '@/utils/workingDayCalculator';
import { toast } from 'sonner';
import SectionHeader from '@/components/SectionHeader';
import { useRealtime } from '@/context/RealtimeContext';
import { useZoomModal } from '@/hooks/useZoomModal';

// Sections like "Contact 1", "Reschedule Contact 2", "Night 2 Follow-up", "Feedback Contact 3"
const CONTACT_SECTION = /^(contact|reschedule contact|second reschedule contact|night|feedback contact)\s*\d+/i;

const inputClass =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:opacity-50';

// Sections a rep opens on demand (they are never part of the happy path)
const OPTIONAL_SECTION = /reschedule|redo|re-engagement/i;

const notEmpty = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  return value !== undefined && value !== null && value !== '' && value !== false;
};

// The stage that follows this one in its pipeline (null on the last stage)
export const getNextStage = (stageName, pipelineType) => {
  const stages = pipelineType === 'tt' ? TT_STAGES : SS_STAGES;
  const index = stages.indexOf(stageName);
  if (index < 0) return null;
  const next = stages[index + 1];
  return next && next !== 'Lost/Cancelled' ? next : null;
};

export const buildSections = (stageConfig) => {
  const grouped = [];
  stageConfig.fields.forEach((field) => {
    const name = field.section || stageConfig.stageName;
    const last = grouped[grouped.length - 1];
    if (last && last.name === name) last.fields.push(field);
    else grouped.push({ name, fields: [field], contact: CONTACT_SECTION.test(name) });
  });
  return grouped;
};

const sectionDateField = (section) => section.fields.find((f) => f.type === FIELD_TYPES.DATE);

// The first card of each SS Schedule group carries the group name, the rest are just "Contact N"
const sectionTitle = (name, stageName = 'SS Schedule') => {
  const lead = stageName === 'TT Schedule' ? 'Titration' : 'Sleep Study';
  if (name === 'Contact 1') return `${lead} → Contact 1`;
  if (name === 'Reschedule Contact 1') return `Reschedule ${lead} → Contact 1`;
  if (name === 'Second Reschedule Contact 1') return `Second Reschedule ${lead} → Contact 1`;
  const reschedule = name.match(/^(?:Second )?Reschedule Contact (\d+)$/);
  return reschedule ? `Contact ${reschedule[1]}` : name;
};
const sectionDecisionField = (section) =>
  section.fields.find((f) => f.type === FIELD_TYPES.SELECT && !f.visibleIf);

// A contact counts as attempted once it has a date and an outcome
const sectionAttempted = (section, data) => {
  const dateField = sectionDateField(section);
  const decisionField = sectionDecisionField(section);
  const hasDate = dateField ? notEmpty(data[dateField.id]) : true;
  const hasDecision = decisionField ? notEmpty(data[decisionField.id]) : true;
  return hasDate && hasDecision;
};

const sectionHasData = (section, data) =>
  section.fields.some((f) => f.type !== FIELD_TYPES.AUTO_CALC && notEmpty(data[f.id]));

// Number of days/hours held in an auto-calc value like "6 working days"
const numericValue = (value) => {
  if (typeof value === 'number') return value;
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

const sectionDecisionValue = (section, data) => {
  const decision = sectionDecisionField(section);
  return decision ? data[decision.id] : undefined;
};

/**
 * SS Schedule rules (confirmed with the team):
 *  - Contact 1 is always open; Contact N only opens once Contact N-1 was answered "No" AND saved.
 *  - Sleep Study Schedule opens as soon as any contact says "Yes" and sits between the contacts
 *    and the reschedule.
 *  - Reschedule only opens once the Sleep Study Schedule was SAVED with data (so never on a first
 *    open, and never while the sleep study is still being captured).
 *  - The reschedule contacts behave exactly like the contacts above and open their own second
 *    Sleep Study Schedule once one of them says "Yes".
 */
const scheduleGating = (sections, data, savedData, visitCount = 1) => {
  const gating = {};
  const group = (prefix) => sections.filter((s) => s.name.startsWith(prefix));
  const contacts = sections.filter((s) => /^Contact \d/.test(s.name));
  const reschedules = group('Reschedule Contact');
  const secondReschedules = group('Second Reschedule Contact');
  // The booking block of each attempt: "Sleep Study Schedule" for SS, "TT Schedule" for TT
  const attemptSections = [...contacts, ...reschedules, ...secondReschedules];
  const bookingSection = sections.find(
    (s) => !attemptSections.includes(s) && /Schedule$/.test(s.name)
  );
  const rescheduleBooking = sections.find((s) => s.name.endsWith('(Reschedule)'));
  const secondRescheduleBooking = sections.find((s) => s.name.endsWith('(Second Reschedule)'));
  const anyYes = (list) =>
    list
      .flatMap((s) => s.fields.filter((f) => f.label === 'Appointment Made?').map((f) => f.id))
      .some((id) => data[id] === 'Yes');

  const attemptGate = (list, section, available) => {
    const index = list.indexOf(section);
    if (sectionHasData(section, data)) return { visible: true };
    if (!available) return { visible: false, hidden: true };
    if (index === 0) return { visible: true };
    const previous = list[index - 1];
    if (sectionDecisionValue(previous, data) !== 'No') {
      return { visible: false, hidden: true, reason: `Opens when ${previous.name} is answered "No"` };
    }
    if (!sectionHasData(previous, savedData)) {
      return { visible: false, reason: `Opens once ${previous.name} is saved` };
    }
    return { visible: true };
  };

  // Only a SAVED booking opens the reschedule (or reschedule data already captured)
  const bookingSaved = Boolean(bookingSection) && sectionHasData(bookingSection, savedData);
  const rescheduleAvailable = bookingSaved || reschedules.some((s) => sectionHasData(s, data));

  // The second reschedule only exists for a case that came BACK to the stage and already holds
  // first-reschedule data
  const firstRescheduleCaptured =
    (Boolean(rescheduleBooking) && sectionHasData(rescheduleBooking, savedData)) ||
    reschedules.some((s) => sectionHasData(s, savedData));
  const secondRescheduleAvailable =
    (visitCount >= 2 && rescheduleAvailable && firstRescheduleCaptured) ||
    secondReschedules.some((s) => sectionHasData(s, data));

  sections.forEach((section) => {
    if (contacts.includes(section)) {
      gating[section.name] = attemptGate(contacts, section, true);
      return;
    }
    if (secondReschedules.includes(section)) {
      gating[section.name] = attemptGate(secondReschedules, section, secondRescheduleAvailable);
      return;
    }
    if (reschedules.includes(section)) {
      gating[section.name] = attemptGate(reschedules, section, rescheduleAvailable);
      return;
    }
    if (section === bookingSection) {
      // Driven by the contacts only — an auto-filled "Today's Date" must not keep it open after
      // the rep corrects a contact back to "No"
      const open = anyYes(contacts) || sectionHasData(section, savedData);
      gating[section.name] = open ? { visible: true } : { visible: false, hidden: true };
      return;
    }
    if (section === rescheduleBooking) {
      const open = sectionHasData(section, savedData) || (rescheduleAvailable && anyYes(reschedules));
      gating[section.name] = open ? { visible: true } : { visible: false, hidden: true };
      return;
    }
    if (section === secondRescheduleBooking) {
      const open =
        sectionHasData(section, savedData) ||
        (secondRescheduleAvailable && anyYes(secondReschedules));
      gating[section.name] = open ? { visible: true } : { visible: false, hidden: true };
      return;
    }
    gating[section.name] = { visible: true };
  });

  return gating;
};

/**
 * SS Feedback (TT stage 1): a contact only opens once the previous one has been SAVED, so each
 * attempt is captured and locked before the next call is logged.
 */
const ssFeedbackGating = (sections, data, savedData) => {
  const gating = {};
  const contacts = sections.filter((s) => s.contact);
  sections.forEach((section) => {
    if (!section.contact) {
      gating[section.name] = { visible: true };
      return;
    }
    const index = contacts.indexOf(section);
    if (index === 0 || sectionHasData(section, data)) {
      gating[section.name] = { visible: true };
      return;
    }
    const previous = contacts[index - 1];
    gating[section.name] = sectionHasData(previous, savedData)
      ? { visible: true }
      : { visible: false, reason: `Opens once ${previous.name} is saved` };
  });
  return gating;
};

/**
 * Progressive disclosure: Contact 1 is always open, Contact N opens once Contact N-1 has a date
 * and an outcome, and the sections after the contacts stay closed until the first contact is made.
 */
export const getSectionGating = (stageConfig, data = {}, revealed = [], options = {}) => {
  const sections = buildSections(stageConfig);
  const savedData = options.savedData || data;

  if (stageConfig.stageName === 'SS Schedule' || stageConfig.stageName === 'TT Schedule') {
    return scheduleGating(sections, data, savedData, options.visitCount || 1);
  }

  if (stageConfig.stageName === 'SS Feedback') {
    return ssFeedbackGating(sections, data, savedData);
  }

  const contacts = sections.filter((s) => s.contact);
  const firstContact = contacts[0];
  const gating = {};

  sections.forEach((section) => {
    if (section.contact) {
      const index = contacts.indexOf(section);
      const previous = contacts[index - 1];
      if (index === 0 || sectionHasData(section, data)) {
        gating[section.name] = { visible: true };
      } else if (sectionAttempted(previous, data)) {
        gating[section.name] = { visible: true };
      } else {
        gating[section.name] = {
          visible: false,
          reason: `Opens once ${previous.name} has a date and an outcome`,
        };
      }
      return;
    }

    if (!firstContact) {
      gating[section.name] = { visible: true };
      return;
    }

    if (sectionHasData(section, data) || revealed.includes(section.name)) {
      gating[section.name] = { visible: true };
      return;
    }

    if (OPTIONAL_SECTION.test(section.name)) {
      gating[section.name] = { visible: false, optional: true, reason: 'Only needed in some cases' };
      return;
    }

    const dateField = sectionDateField(firstContact);
    const started = dateField ? notEmpty(data[dateField.id]) : true;
    gating[section.name] = started
      ? { visible: true, soft: true } // shown early, but nothing in it is compulsory yet
      : { visible: false, optional: true, reason: `Opens once ${firstContact.name} is captured` };
  });

  return gating;
};

// A condition is met either by an exact value or by an auto-calc value above a threshold
const conditionMet = (condition, values) => {
  if (condition.greaterThan !== undefined && condition.greaterThan !== null) {
    const current = numericValue(values[condition.fieldId]);
    return current !== null && current > condition.greaterThan;
  }
  return values[condition.fieldId] === condition.value;
};

const visibleByCondition = (field, stageConfig, values, seen = []) => {
  if (!field.visibleIf) return true;
  if (!conditionMet(field.visibleIf, values)) return false;
  if (seen.includes(field.id)) return true;
  const parent = stageConfig.fields.find((f) => f.id === field.visibleIf.fieldId);
  return parent ? visibleByCondition(parent, stageConfig, values, [...seen, field.id]) : true;
};

/** Fields filled vs fields currently on screen — shared with the Kanban card badge. */
export const computeStageCompletion = (stageConfig, data = {}) => {
  if (!stageConfig?.fields?.length) return { filled: 0, total: 0, percent: 0 };
  const values = data && typeof data === 'object' ? data : {};
  const gating = getSectionGating(stageConfig, values);

  const visible = stageConfig.fields.filter((field) => {
    if (field.hidden) return false;
    if (field.type === FIELD_TYPES.AUTO_CALC) return false;
    const sectionGate = gating[field.section || stageConfig.stageName];
    if (sectionGate?.visible === false) return false;
    if (sectionGate?.soft) return false; // not started yet — keeps the card badge on the happy path
    return visibleByCondition(field, stageConfig, values);
  });

  const filled = visible.filter((field) => notEmpty(values[field.id])).length;
  return {
    filled,
    total: visible.length,
    percent: visible.length ? Math.round((filled / visible.length) * 100) : 0,
  };
};

// A study only counts as scheduled when the attempt the rep is working in has an
// "Appointment Made? = Yes" AND a scheduled date. Later attempts take precedence, so a case that
// was rescheduled cannot advance on the strength of an older booking. Works for SS and TT.
export const scheduleReadiness = (stageConfig, data) => {
  const values = data && typeof data === 'object' ? data : {};
  const sections = buildSections(stageConfig);
  const contacts = sections.filter((s) => /^Contact \d/.test(s.name));
  const reschedules = sections.filter((s) => s.name.startsWith('Reschedule Contact'));
  const seconds = sections.filter((s) => s.name.startsWith('Second Reschedule Contact'));
  const attemptSections = [...contacts, ...reschedules, ...seconds];
  const scheduledDateId = (booking) =>
    booking?.fields.find((f) => /^Scheduled/.test(f.label))?.id;

  const attempts = [
    {
      key: 'first',
      label: 'first attempt',
      contacts,
      dateId: scheduledDateId(
        sections.find((s) => !attemptSections.includes(s) && /Schedule$/.test(s.name))
      ),
    },
    {
      key: 'reschedule',
      label: 'reschedule',
      contacts: reschedules,
      dateId: scheduledDateId(sections.find((s) => s.name.endsWith('(Reschedule)'))),
    },
    {
      key: 'second_reschedule',
      label: 'second reschedule',
      contacts: seconds,
      dateId: scheduledDateId(sections.find((s) => s.name.endsWith('(Second Reschedule)'))),
    },
  ]
    .filter((attempt) => attempt.contacts.length)
    .map((attempt) => ({
      ...attempt,
      ids: attempt.contacts.flatMap((section) =>
        section.fields.filter((f) => f.label === 'Appointment Made?').map((f) => f.id)
      ),
    }));

  const hasData = (attempt) =>
    attempt.ids.some((id) => notEmpty(values[id])) || notEmpty(values[attempt.dateId]);
  const active = [...attempts].reverse().find(hasData) || attempts[0];
  if (!active) return { label: 'first attempt', hasYes: false, hasDate: false, ready: false };
  const hasYes = active.ids.some((id) => values[id] === 'Yes');
  const hasDate = notEmpty(values[active.dateId]);
  return { attempt: active.key, label: active.label, hasYes, hasDate, ready: hasYes && hasDate };
};

// Stages a case can come back to: the earlier answers are archived and shown read-only above a
// blank form, so each visit is captured on its own.
const ATTEMPT_VISIT_COUNTERS = {
  'SS Appointment Confirmation': 'ss_appointment_confirmation_visit_count',
  'SS Facilitation': 'ss_facilitation_visit_count',
  'SS Feedback': 'ss_feedback_visit_count',
  'TT Facilitation': 'tt_facilitation_visit_count',
};

// Scheduling stages count their visits: a second visit unlocks the second reschedule block
const SCHEDULE_VISIT_COUNTERS = {
  'SS Schedule': 'ss_schedule_visit_count',
  'TT Schedule': 'tt_schedule_visit_count',
};

// The last contact of an attempt stays editable so a rep can keep working it
const OPEN_ENDED_SECTION = /Contact 6$|^Feedback Contact 5$/;

export const StageModal = ({
  isOpen,
  onClose,
  onSave,
  onMoveToLostCancelled,
  onDuplicateToTt = null,
  stageConfig,
  caseData = null,
  referralDate = null,
  pipelineType = 'ss',
}) => {
  const [formData, setFormData] = useState({});
  const [savedData, setSavedData] = useState({});
  const [errors, setErrors] = useState({});
  const [revealedSections, setRevealedSections] = useState([]);
  const [showCancellationConfirm, setShowCancellationConfirm] = useState(false);
  const [cancellationField, setCancellationField] = useState(null);
  const [cancelAfterSave, setCancelAfterSave] = useState(false);
  const [showDuplicatePrompt, setShowDuplicatePrompt] = useState(false);
  const [archivedAttempts, setArchivedAttempts] = useState([]);
  const [freshAttempt, setFreshAttempt] = useState(false);
  const [sendBackPrompt, setSendBackPrompt] = useState(null);
  const { zoom, containerRef, zoomIn, zoomOut, resetZoom } = useZoomModal(isOpen);
  const [saving, setSaving] = useState(false);

  // Load existing stage data (kept whole so nothing is lost on save)
  useEffect(() => {
    if (!isOpen) return;
    const existing = caseData?.[stageConfig.dataFieldName];
    const stored = existing && typeof existing === 'object' ? { ...existing } : {};
    let history = Array.isArray(stored.previous_attempts) ? stored.previous_attempts : [];
    delete stored.previous_attempts;
    // A case that came BACK to this stage starts a blank form: the earlier attempt is archived
    // and shown read-only above it, and only the new data is saved.
    const counterField = ATTEMPT_VISIT_COUNTERS[stageConfig.stageName];
    const visit = Number(counterField ? caseData?.[counterField] : 1) || 1;
    const attempt = Number(stored.attempt_number) || 1;
    const restart = Boolean(counterField) && visit > attempt && Object.keys(stored).length > 0;
    if (restart) history = [...history, stored];
    const loaded = restart ? {} : stored;
    setArchivedAttempts(history);
    setFreshAttempt(restart);
    setFormData(loaded);
    setSavedData(loaded);
    setErrors({});
    setRevealedSections([]);
    setCancelAfterSave(false);
    setCancellationField(null);
    setShowCancellationConfirm(false);
    setShowDuplicatePrompt(false);
    setSendBackPrompt(null);
  }, [isOpen, caseData, stageConfig]);

  const sections = useMemo(() => buildSections(stageConfig), [stageConfig]);

  const caseTin = caseData?.tin_number;
  const { startEditing, stopEditing, closeCase, editorOf, onCaseChange } = useRealtime();
  const [remoteChange, setRemoteChange] = useState(null);
  const liveRef = useRef({ closeCase });
  liveRef.current.closeCase = closeCase;

  // Live editing indicators: tell the room which field has focus, and clear once on close
  useEffect(() => {
    if (!isOpen || !caseTin) return;
    setRemoteChange(null);
    return () => liveRef.current.closeCase(caseTin);
  }, [isOpen, caseTin]);

  useEffect(
    () =>
      onCaseChange((event) => {
        if (isOpen && caseTin && event.case_tin === caseTin) setRemoteChange(event);
      }),
    [onCaseChange, isOpen, caseTin]
  );

  // How many times this case has entered the scheduling stage (set by the backend on stage entry)
  const scheduleCounterField = SCHEDULE_VISIT_COUNTERS[stageConfig.stageName];
  const visitCount = Number(scheduleCounterField ? caseData?.[scheduleCounterField] : 1) || 1;
  const secondVisit = Boolean(scheduleCounterField) && visitCount >= 2;

  // A section that was already saved with data is read-only (point 10).
  // Contact 6 is the open-ended attempt, so it never locks — except on a second visit to
  // SS Schedule, where everything before the second reschedule is history.
  const lockedSections = useMemo(
    () =>
      sections
        .filter((s) => {
          if (!sectionHasData(s, savedData)) return false;
          // SS Reports is filled in over days (summary → scoring → report), so it never locks
          if (stageConfig.stageName === 'SS Reports') return false;
          if (/^Second Reschedule/.test(s.name)) return false;
          if (s.name === 'Sleep Study Schedule (Second Reschedule)') return false;
          if (OPEN_ENDED_SECTION.test(s.name)) return secondVisit;
          return true;
        })
        .map((s) => s.name),
    [sections, savedData, secondVisit, stageConfig]
  );

  const isSectionLocked = (name) => lockedSections.includes(name);

  const gating = useMemo(
    () => getSectionGating(stageConfig, formData, revealedSections, { savedData, visitCount }),
    [stageConfig, formData, revealedSections, savedData, visitCount]
  );

  // Point 5: the first date of every contact section — and the "Today's Date" of the sleep study
  // blocks — starts on today, then locks on save. Fields marked autoFillToday do the same.
  // It runs when the set of open sections changes, never on every keystroke: re-rendering the
  // form while a native date picker was open is what made dates need a second click.
  // An auto-filled field only takes today's date once it is actually on screen, so a "No" answer
  // never leaves a facilitation date behind
  const autoFillReady = (field, values) =>
    Boolean(field.autoFillToday) &&
    (!field.visibleIf ||
      field.visibleIf.value === undefined ||
      values[field.visibleIf.fieldId] === field.visibleIf.value);

  const autoFillKey = useMemo(
    () =>
      sections
        .filter((s) => gating[s.name]?.visible !== false && !lockedSections.includes(s.name))
        .flatMap((s) => [s.name, ...s.fields.filter((f) => autoFillReady(f, formData)).map((f) => f.id)])
        .join('|'),
    [sections, gating, lockedSections, formData]
  );

  useEffect(() => {
    if (!isOpen) return;
    const today = new Date().toISOString().split('T')[0];
    const open = sections.filter(
      (s) => gating[s.name]?.visible !== false && !lockedSections.includes(s.name)
    );
    setFormData((prev) => {
      const next = { ...prev };
      let changed = false;
      // Only fills blanks — never overwrites a value that was already captured
      const fill = (id) => {
        if (!notEmpty(next[id])) {
          next[id] = today;
          changed = true;
        }
      };
      open.forEach((section) => {
        section.fields.forEach((field) => {
          if (autoFillReady(field, prev)) fill(field.id);
        });
        if (!section.contact && !/^Sleep Study Schedule/.test(section.name)) return;
        const dateField = sectionDateField(section);
        if (dateField) fill(dateField.id);
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, autoFillKey]);

  const completion = useMemo(() => computeStageCompletion(stageConfig, formData), [stageConfig, formData]);


  // Consecutive contact/night sections are rendered as one horizontal row of cards
  const blocks = useMemo(() => {
    const result = [];
    sections.forEach((section) => {
      const last = result[result.length - 1];
      if (section.contact && last?.type === 'contacts') last.sections.push(section);
      else if (section.contact) result.push({ type: 'contacts', sections: [section] });
      else result.push({ type: 'section', section });
    });
    return result;
  }, [sections]);

  // Looks a value up in this form first, then on the stage it belongs to, then on the case itself.
  // This is what lets Contact 2's lead time read Contact 1's date and TT read the SS history.
  const resolveValue = (fieldId, stageKey = null) => {
    if (!fieldId) return undefined;
    if (stageKey) {
      const source = stageKey === 'case' ? caseData : caseData?.[stageKey];
      return source && typeof source === 'object' ? source[fieldId] : undefined;
    }
    if (notEmpty(formData[fieldId])) return formData[fieldId];
    if (caseData) {
      if (notEmpty(caseData[fieldId])) return caseData[fieldId];
      const fromOtherStage = Object.keys(caseData).find(
        (key) =>
          key.endsWith('_data') &&
          caseData[key] &&
          typeof caseData[key] === 'object' &&
          notEmpty(caseData[key][fieldId])
      );
      if (fromOtherStage) return caseData[fromOtherStage][fieldId];
    }
    if (fieldId === 'referral_date' && referralDate) return referralDate;
    return undefined;
  };

  // Date of the most recent SS Feedback contact where feedback was actually provided
  const lastSsFeedbackYesDate = () => {
    const feedback = caseData?.ss_feedback_data;
    if (feedback && typeof feedback === 'object') {
      for (let i = 5; i >= 1; i -= 1) {
        if (feedback[`feedback_contact_${i}_feedback_provided`] === 'Yes') {
          return feedback[`feedback_contact_${i}_date`];
        }
      }
    }
    // SS Feedback belongs to TT now, so an SS case measures from its SS Follow up date
    return caseData?.ss_followup_date;
  };

  const resolveAutoDate = (fieldId, stageKey, special) => {
    if (special === 'last_ss_feedback_yes') return lastSsFeedbackYesDate();
    return resolveValue(fieldId, stageKey);
  };

  const calculateAutoField = (field) => {
    if (field.type !== FIELD_TYPES.AUTO_CALC || !field.autoCalcFrom) return '';
    const { startField, startStage, startFrom, endField, endStage, format } = field.autoCalcFrom;
    const startDate = resolveAutoDate(startField, startStage, startFrom);
    const endDate = resolveAutoDate(endField, endStage, null);
    if (!startDate || !endDate) return '';

    if (format === 'hours') {
      const diffMs = new Date(endDate) - new Date(startDate);
      return Number.isNaN(diffMs) ? '' : `${Math.floor(diffMs / (1000 * 60 * 60))} hours`;
    }
    return formatWorkingDays(calculateWorkingDays(startDate, endDate));
  };

  // A field is only visible when its condition holds AND the field that condition points at is
  // itself visible — so switching a parent answer back hides the whole chain below it.
  const isFieldVisible = (field, seen = []) => {
    if (field.hidden) return false;
    if (gating[field.section || stageConfig.stageName]?.visible === false) return false;
    if (!field.visibleIf) return true;
    const { fieldId, greaterThan, value } = field.visibleIf;
    if (greaterThan !== undefined && greaterThan !== null) {
      const current = conditionValue(fieldId);
      if (!(typeof current === 'number' && current > greaterThan)) return false;
    } else if (formData[fieldId] !== value) {
      return false;
    }
    if (seen.includes(field.id)) return true;
    const parent = stageConfig.fields.find((f) => f.id === fieldId);
    return parent ? isFieldVisible(parent, [...seen, field.id]) : true;
  };

  const handleFieldChange = (fieldId, value) => {
    const field = stageConfig.fields.find((f) => f.id === fieldId);
    setFormData((prev) => {
      const next = { ...prev, [fieldId]: value };
      // Switching funding method clears the answers it drives, so no stale option survives
      (field?.resetOnChange || []).forEach((dependentId) => {
        next[dependentId] = '';
      });
      return next;
    });
    setErrors((prev) => (prev[fieldId] ? { ...prev, [fieldId]: undefined } : prev));

    // Reasons marked confirmAfterSave only offer the move once the stage has been saved
    if (field?.triggersLostCancelled && !field.confirmAfterSave && value) {
      setCancellationField(fieldId);
      setShowCancellationConfirm(true);
    }
  };

  // Value used for requiredIf checks (auto-calc fields resolve to their number of days/hours)
  const conditionValue = (fieldId) => {
    const field = stageConfig.fields.find((f) => f.id === fieldId);
    if (field?.type === FIELD_TYPES.AUTO_CALC) {
      const calculated = calculateAutoField(field);
      const parsed = parseInt(calculated, 10);
      return Number.isNaN(parsed) ? null : parsed;
    }
    return formData[fieldId];
  };

  const isConditionallyRequired = (field) => {
    if (!field.requiredIf) return false;
    const { fieldId, value, greaterThan } = field.requiredIf;
    const current = conditionValue(fieldId);
    if (greaterThan !== undefined && greaterThan !== null) {
      return typeof current === 'number' && current > greaterThan;
    }
    return current === value;
  };

  const isFieldRequired = (field) => field.required || isConditionallyRequired(field);

  const validateForm = () => {
    const next = {};
    stageConfig.fields.forEach((field) => {
      const sectionName = field.section || stageConfig.stageName;
      if (!isFieldVisible(field)) return;
      if (field.type === FIELD_TYPES.AUTO_CALC) return;
      // Already saved sections are read-only, so they are not re-validated
      if (isSectionLocked(sectionName)) return;
      // Sections that were opened early are optional until the rep starts filling them in
      if (gating[sectionName]?.soft) return;
      if (!isFieldRequired(field)) return;
      if (!notEmpty(formData[field.id])) {
        next[field.id] = `${field.label} is required`;
      }
    });
    return next;
  };

  const validate = () => {
    const next = validateForm();
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  // Auto-calculated values are stored too, so exports/reports can read them
  const buildPayload = () => {
    const existing = caseData?.[stageConfig.dataFieldName];
    // A new attempt starts from nothing, so the archived answers never bleed into it
    const base = !freshAttempt && existing && typeof existing === 'object' ? existing : {};
    // A section that is hidden again (e.g. a contact corrected back to "No") keeps nothing behind
    const hiddenFieldIds = new Set(
      stageConfig.fields
        .filter((field) => gating[field.section || stageConfig.stageName]?.hidden)
        .map((field) => field.id)
    );
    const entered = Object.fromEntries(
      Object.entries(formData).filter(([key]) => !hiddenFieldIds.has(key))
    );
    const computed = {};
    stageConfig.fields.forEach((field) => {
      if (field.type === FIELD_TYPES.AUTO_CALC && !hiddenFieldIds.has(field.id)) {
        const value = calculateAutoField(field);
        if (value) computed[field.id] = value;
      }
    });
    const payload = { ...base, ...entered, ...computed };
    const counterField = ATTEMPT_VISIT_COUNTERS[stageConfig.stageName];
    if (counterField) {
      payload.attempt_number = Number(caseData?.[counterField]) || 1;
      if (archivedAttempts.length) payload.previous_attempts = archivedAttempts;
    }
    return payload;
  };

  const nextStage = getNextStage(stageConfig.stageName, pipelineType);

  // SS Schedule can only advance once the attempt in play is actually booked; SS Appointment
  // Confirmation needs the confirmation itself on top of that booking.
  const advanceChecklist = useMemo(() => {
    const stage = stageConfig.stageName;
    if (stage === 'SS Facilitation') {
      const done = formData.did_study_take_place === 'Yes';
      return { items: [{ label: 'Did Study Take Place? is "Yes"', done }], ready: done };
    }
    if (stage === 'TT Facilitation') {
      const done = formData.did_titration_take_place === 'Yes';
      return { items: [{ label: 'Did Titration Take Place? is "Yes"', done }], ready: done };
    }
    if (stage === 'SS Feedback') {
      const done = [1, 2, 3, 4, 5].some(
        (n) => formData[`feedback_contact_${n}_feedback_provided`] === 'Yes'
      );
      return {
        items: [{ label: 'At least one contact has "Feedback Provided?" = Yes', done }],
        ready: done,
      };
    }
    if (stage === 'SS Reports') {
      const done = notEmpty(formData.date_report_compiled);
      return {
        items: [{ label: 'Date Report Compiled & Sent is captured', done }],
        ready: done,
      };
    }
    const scheduleStage = stage === 'SS Schedule' || stage === 'TT Schedule';
    if (!scheduleStage && stage !== 'SS Appointment Confirmation') return null;
    const readiness = scheduleStage
      ? scheduleReadiness(stageConfig, formData)
      : scheduleReadiness(SS_SCHEDULE_CONFIG, caseData?.ss_schedule_data);
    const items = [];
    if (stage === 'SS Appointment Confirmation') {
      items.push({
        label: 'Appointment Confirmed? is "Yes"',
        done: formData.appointment_confirmed === 'Yes',
      });
    }
    items.push({
      label: `A ${readiness.label} contact has "Appointment Made?" = Yes`,
      done: readiness.hasYes,
    });
    items.push({ label: 'Scheduled Sleep Study Date is captured', done: readiness.hasDate });
    return { items, ready: items.every((item) => item.done) };
  }, [stageConfig, formData, caseData]);

  const canAdvance = !advanceChecklist || advanceChecklist.ready;
  const advanceWarning = advanceChecklist?.items.find((item) => !item.done)?.label;

  const handleSave = async (advanceTo = null) => {
    if (!validate()) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      await onSave(payload, advanceTo);
      toast.success(
        advanceTo ? `Saved and moved to ${advanceTo}` : `${stageConfig.stageName} data saved`
      );
      // A cancellation reason captured here asks — after the save, never before — whether the case
      // should leave the stage
      const cancelReason =
        !advanceTo &&
        stageConfig.fields.find(
          (field) =>
            field.triggersLostCancelled &&
            field.confirmAfterSave &&
            isFieldVisible(field) &&
            notEmpty(formData[field.id])
        );
      if (cancelReason) {
        setSavedData(payload);
        setCancellationField(cancelReason.id);
        setCancelAfterSave(true);
        setShowCancellationConfirm(true);
        return;
      }
      // A delay reason offers the trip back for rescheduling, also only after the save
      const sendBack =
        !advanceTo &&
        stageConfig.fields.find(
          (field) => field.offersSendBackTo && isFieldVisible(field) && notEmpty(formData[field.id])
        );
      if (sendBack) {
        setSavedData(payload);
        setSendBackPrompt({ stage: sendBack.offersSendBackTo, reason: formData[sendBack.id] });
        return;
      }
      if (stageConfig.stageName === 'SS Follow up' && onDuplicateToTt) {
        setSavedData(payload);
        setShowDuplicatePrompt(true);
        return;
      }
      onClose();
    } catch (error) {
      console.error('Error saving stage data:', error);
      toast.error(error?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // "No" on the outcome sends the case back for rescheduling (which counts as a new visit there)
  const handleSendBack = async () => {
    setSaving(true);
    try {
      await onSave(buildPayload(), sendBackPrompt.stage);
      setSendBackPrompt(null);
      onClose();
    } catch (error) {
      console.error('Error sending the case back:', error);
      toast.error(error?.response?.data?.detail || 'Failed to send the case back');
    } finally {
      setSaving(false);
    }
  };

  // Saving SS Follow up offers the jump into Titration (SS Feedback is its first stage)
  const handleDuplicateToTt = async () => {    setSaving(true);
    try {
      await onDuplicateToTt();
      setShowDuplicatePrompt(false);
      onClose();
    } catch (error) {
      console.error('Error copying case to TT:', error);
      toast.error(error?.response?.data?.detail || 'Failed to copy the case to TT');
    } finally {
      setSaving(false);
    }
  };

  const confirmMoveToCancelled = async () => {
    setSaving(true);
    try {
      await onMoveToLostCancelled({
        ...buildPayload(),
        cancelled_at_stage: caseData?.current_stage || stageConfig.stageName,
        cancellation_reason: formData[cancellationField],
      });
      toast.success('Case moved to Lost/Cancelled');
      setShowCancellationConfirm(false);
      setCancelAfterSave(false);
      onClose();
    } catch (error) {
      console.error('Error moving to Lost/Cancelled:', error);
      toast.error(error?.response?.data?.detail || 'Failed to move case');
    } finally {
      setSaving(false);
    }
  };

  const dismissCancellation = () => {
    // After a save the reason is already stored, so it stays — the case simply keeps its stage
    if (cancelAfterSave) {
      setShowCancellationConfirm(false);
      setCancellationField(null);
      setCancelAfterSave(false);
      onClose();
      return;
    }
    // Clear the reason so the case is not silently marked as cancelled
    if (cancellationField) {
      setFormData((prev) => ({ ...prev, [cancellationField]: '' }));
    }
    setShowCancellationConfirm(false);
    setCancellationField(null);
  };

  if (!isOpen) return null;

  const renderField = (field, locked = false) => {
    const value = formData[field.id] ?? '';
    const testId = `stage-field-${field.id}`;
    const errorClass = errors[field.id] ? 'border-red-500' : '';

    if (field.type === FIELD_TYPES.AUTO_CALC) {
      return (
        <div className="rounded-md border border-input bg-muted px-3 py-2" data-testid={testId}>
          <p className="text-sm font-medium text-muted-foreground">
            {calculateAutoField(field) || '—'}
          </p>
        </div>
      );
    }

    if (field.type === FIELD_TYPES.MULTI_SELECT) {
      const selected = Array.isArray(formData[field.id]) ? formData[field.id] : [];
      return (
        <div className="flex flex-wrap gap-1.5" data-testid={testId}>
          {field.options.map((option) => {
            const active = selected.includes(option);
            return (
              <button
                key={option}
                type="button"
                disabled={locked}
                onClick={() =>
                  handleFieldChange(
                    field.id,
                    active ? selected.filter((item) => item !== option) : [...selected, option]
                  )
                }
                className={`px-2.5 py-1 rounded-full border text-xs transition-colors disabled:opacity-60 ${
                  active
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'bg-background border-input hover:bg-muted'
                }`}
                data-testid={`stage-option-${field.id}-${option.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                {option}
              </button>
            );
          })}
        </div>
      );
    }

    if (field.type === FIELD_TYPES.SELECT) {
      const configured = field.dynamicOptions
        ? field.dynamicOptions.byValue[formData[field.dynamicOptions.fieldId]] || []
        : field.options;
      // Keep a previously saved value selectable even if the option list has since changed
      const options = value && !configured.includes(value) ? [...configured, value] : configured;
      return (
        <select
          id={field.id}
          value={value}
          disabled={locked}
          onChange={(e) => handleFieldChange(field.id, e.target.value)}
          className={`${inputClass} ${errorClass}`}
          data-testid={testId}
        >
          <option value="">Select...</option>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      );
    }

    if (field.type === FIELD_TYPES.TEXTAREA) {
      return (
        <textarea
          id={field.id}
          rows={3}
          placeholder={field.placeholder}
          value={value}
          disabled={locked}
          onChange={(e) => handleFieldChange(field.id, e.target.value)}
          className={`w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:opacity-60 ${errorClass}`}
          data-testid={testId}
        />
      );
    }

    if (field.type === FIELD_TYPES.CHECKBOX) {
      return (
        <label className="flex items-center gap-2 text-sm h-10">
          <input
            id={field.id}
            type="checkbox"
            checked={Boolean(value)}
            disabled={locked}
            onChange={(e) => handleFieldChange(field.id, e.target.checked)}
            data-testid={testId}
          />
          {field.placeholder || 'Yes'}
        </label>
      );
    }

    if (field.type === FIELD_TYPES.NUMBER) {
      return (
        <Input
          id={field.id}
          type="number"
          min={field.min ?? 1}
          max={field.max ?? 999}
          placeholder={field.placeholder}
          value={value}
          disabled={locked}
          onChange={(e) => handleFieldChange(field.id, e.target.value)}
          className={errorClass}
          data-testid={testId}
        />
      );
    }

    return (
      <Input
        id={field.id}
        type={field.type === FIELD_TYPES.DATE ? 'date' : 'text'}
        placeholder={field.placeholder}
        value={value}
        disabled={locked}
        onChange={(e) => handleFieldChange(field.id, e.target.value)}
        className={errorClass}
        data-testid={testId}
      />
    );
  };

  const showRequiredMark = (field) =>
    isFieldRequired(field) && !gating[field.section || stageConfig.stageName]?.soft;

  const renderFieldBlock = (field) => {
    const locked =
      isSectionLocked(field.section || stageConfig.stageName) ||
      (field.readOnlyAfterSave && notEmpty(savedData[field.id]));
    const editor = caseTin ? editorOf(caseTin, field.id) : null;
    const sectionName = field.section || stageConfig.stageName;
    return (
      <div
        key={field.id}
        className={errors[field.id] ? 'border-l-4 border-red-500 pl-3' : ''}
        onFocus={() =>
          !locked && caseTin && startEditing(caseTin, field.id, `${sectionName} · ${field.label}`)
        }
        onBlur={() => !locked && caseTin && stopEditing(caseTin, field.id)}
      >
        <Label
          htmlFor={field.id}
          className={`text-sm font-medium ${errors[field.id] ? 'text-red-600' : ''}`}
        >
          {field.label}
          {showRequiredMark(field) && !locked && <span className="text-red-500 ml-1">*</span>}
        </Label>
        {renderField(field, locked)}
        {editor && (
          <p
            className="text-[11px] mt-1 flex items-center gap-1 text-amber-700 dark:text-amber-300"
            data-testid={`field-editing-${field.id}`}
          >
            <Pencil className="h-3 w-3 animate-pulse" />
            {editor.name.split(' ')[0]} is editing this
          </p>
        )}
        {field.helpText && <p className="text-xs text-muted-foreground mt-1">{field.helpText}</p>}
        {errors[field.id] && <p className="text-xs text-red-500 mt-1">{errors[field.id]}</p>}
      </div>
    );
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent
          className="max-w-6xl max-h-[90vh] overflow-y-auto"
          data-testid={`stage-modal-${stageConfig.dataFieldName}`}
        >
          <DialogHeader>
            <DialogTitle className="flex flex-wrap items-center gap-3">
              <span>
                {stageConfig.stageName}
                {caseData ? ` — ${caseData.name} ${caseData.surname}` : ''}
              </span>
              <span className="flex items-center gap-1 text-xs font-normal text-muted-foreground">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={zoomOut}
                  title="Zoom out"
                  data-testid="stage-modal-zoom-out"
                >
                  <Minus className="h-3.5 w-3.5" />
                </Button>
                <button
                  type="button"
                  onClick={resetZoom}
                  className="min-w-[3.5rem] tabular-nums hover:underline"
                  title="Reset zoom"
                  data-testid="stage-modal-zoom-level"
                >
                  {zoom}%
                </button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={zoomIn}
                  title="Zoom in"
                  data-testid="stage-modal-zoom-in"
                >
                  <Plus className="h-3.5 w-3.5" />
                </Button>
                <span className="hidden sm:inline">Ctrl+scroll or pinch to zoom</span>
              </span>
            </DialogTitle>
          </DialogHeader>

          <div className="mb-2" data-testid="stage-modal-progress">
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
              <span>
                {completion.filled}/{completion.total} fields filled
              </span>
              <span className="font-semibold">{completion.percent}%</span>
            </div>
            <div className="h-2 w-full rounded bg-muted overflow-hidden">
              <div
                className={`h-2 rounded transition-all ${
                  completion.percent === 100 ? 'bg-emerald-600' : 'bg-blue-600'
                }`}
                style={{ width: `${completion.percent}%` }}
              />
            </div>
          </div>

          {secondVisit && (
            <div
              className="mb-3 rounded-md border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-xs text-amber-900 dark:text-amber-200"
              data-testid="stage-modal-second-visit"
            >
              Visit {visitCount} to {stageConfig.stageName} — the earlier attempts are read-only,
              capture the second reschedule below.
            </div>
          )}

          {remoteChange && (
            <div
              className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-blue-300 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/50 px-3 py-2"
              data-testid="stage-modal-remote-change"
            >
              <RefreshCw className="h-3.5 w-3.5 text-blue-600 dark:text-blue-300" />
              <p className="text-xs text-blue-800 dark:text-blue-200">
                {remoteChange.by} just saved this case — your copy may be out of date.
              </p>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs ml-auto"
                onClick={onClose}
                data-testid="stage-modal-remote-reload"
              >
                Close and reopen
              </Button>
            </div>
          )}

          {archivedAttempts.length > 0 && (
            <div className="mb-4 space-y-3" data-testid="stage-modal-previous-attempts">
              {archivedAttempts.map((attempt, index) => {
                const captured = stageConfig.fields.filter(
                  (field) =>
                    !field.hidden &&
                    field.type !== FIELD_TYPES.AUTO_CALC &&
                    notEmpty(attempt[field.id])
                );
                return (
                  <div
                    key={`attempt-${index}`}
                    className="rounded-lg border-l-4 border-amber-500 bg-muted/60 p-4"
                    data-testid={`stage-modal-previous-attempt-${index + 1}`}
                  >
                    <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300 mb-2">
                      <Lock className="h-3.5 w-3.5" />
                      Attempt {index + 1} — read-only
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {captured.map((field) => (
                        <div key={field.id}>
                          <p className="text-[11px] text-muted-foreground">{field.label}</p>
                          <p className="text-sm font-medium">{String(attempt[field.id])}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              {freshAttempt && (
                <p className="text-xs text-muted-foreground" data-testid="stage-modal-fresh-attempt">
                  Capture the new attempt below — only the new answers are saved.
                </p>
              )}
            </div>
          )}

          {/* Zoom container: Ctrl/Cmd+scroll on desktop, pinch on touch */}
          <div ref={containerRef} className="overflow-x-auto" data-testid="stage-modal-zoom-container">
          <div
            className="space-y-8 origin-top-left transition-transform"
            style={{ transform: `scale(${zoom / 100})`, width: `${10000 / zoom}%` }}
            data-testid="stage-modal-form"
          >
            {blocks.map((block, blockIndex) => {
              if (block.type === 'contacts') {
                const open = block.sections.filter((section) => gating[section.name]?.visible !== false);
                const cards = open
                  .map((section) => ({ section, visible: section.fields.filter((f) => isFieldVisible(f)) }))
                  .filter(({ visible }) => visible.length > 0);
                const locked = block.sections.find(
                  (section) => gating[section.name]?.visible === false && !gating[section.name]?.hidden
                );
                if (cards.length === 0 && !locked) return null;
                return (
                  <div
                    key={`contacts-${blockIndex}`}
                    className="flex flex-col md:flex-row gap-4 md:overflow-x-auto md:pb-2"
                    data-testid="stage-contact-row"
                  >
                    {cards.map(({ section, visible }) => (
                      <div
                        key={section.name}
                        className="w-full md:w-[19rem] md:shrink-0 rounded-lg border bg-muted/40 p-4"
                        data-testid={`stage-contact-card-${section.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <SectionHeader
                          title={sectionTitle(section.name, stageConfig.stageName)}
                          compact
                          locked={isSectionLocked(section.name)}
                          note={
                            !isSectionLocked(section.name) && sectionAttempted(section, formData)
                              ? 'Done'
                              : null
                          }
                        />
                        <div className="space-y-3">{visible.map(renderFieldBlock)}</div>
                      </div>
                    ))}
                    {locked && (
                      <div
                        className="w-full md:w-[19rem] md:shrink-0 rounded-lg border border-dashed p-4 flex items-center"
                        data-testid={`stage-contact-locked-${locked.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <div className="flex items-start gap-2 text-xs text-muted-foreground">
                          <Lock className="h-4 w-4 mt-0.5 shrink-0" />
                          <p>
                            <span className="font-semibold uppercase tracking-wide">{locked.name}</span>
                            <br />
                            {gating[locked.name]?.reason}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              const { section } = block;
              if (gating[section.name]?.hidden) return null;
              if (gating[section.name]?.visible === false) {
                return (
                  <div
                    key={`locked-${section.name}`}
                    className="rounded-lg border border-dashed p-4 flex flex-wrap items-center justify-between gap-3"
                    data-testid={`stage-section-locked-${section.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <div className="flex items-start gap-2 text-xs text-muted-foreground">
                      <Lock className="h-4 w-4 mt-0.5 shrink-0" />
                      <p>
                        <span className="font-semibold uppercase tracking-wide">{section.name}</span> —{' '}
                        {gating[section.name]?.reason}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setRevealedSections((prev) => [...prev, section.name])}
                      data-testid={`stage-section-reveal-${section.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      Open anyway
                    </Button>
                  </div>
                );
              }

              const visible = section.fields.filter((f) => isFieldVisible(f));
              if (visible.length === 0) return null;
              return (
                <div key={`${section.name}-${section.fields[0].id}`}>
                  <SectionHeader
                    title={sectionTitle(section.name, stageConfig.stageName)}
                    locked={isSectionLocked(section.name)}
                    note={gating[section.name]?.soft ? 'optional until you start it' : null}
                  />
                  {/* SS Reports keeps the captured dates on the left and the lead times on the right */}
                  {stageConfig.stageName === 'SS Reports' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                      <div className="space-y-4">
                        {visible
                          .filter((field) => field.type !== FIELD_TYPES.AUTO_CALC)
                          .map(renderFieldBlock)}
                      </div>
                      <div className="space-y-4">
                        {visible
                          .filter((field) => field.type === FIELD_TYPES.AUTO_CALC)
                          .map(renderFieldBlock)}
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {visible.map(renderFieldBlock)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-end pt-4 border-t">
            {nextStage && !canAdvance && advanceWarning && (
              <p
                className="text-xs text-red-600 dark:text-red-400 sm:mr-auto"
                data-testid="stage-modal-advance-warning"
              >
                {advanceWarning} — required to move to {nextStage}
              </p>
            )}
            <Button variant="outline" onClick={onClose} disabled={saving} data-testid="stage-modal-cancel">
              Cancel
            </Button>
            <Button onClick={() => handleSave()} disabled={saving} data-testid="stage-modal-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
            {nextStage && canAdvance && (
              <Button
                onClick={() => handleSave(nextStage)}
                disabled={saving}
                className="gap-1.5 bg-emerald-600 hover:bg-emerald-700"
                data-testid="stage-modal-save-next"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    Save & move to {nextStage}
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showDuplicatePrompt}
        onOpenChange={(open) => {
          if (!open) {
            setShowDuplicatePrompt(false);
            onClose();
          }
        }}
      >
        <DialogContent className="max-w-sm" data-testid="stage-modal-duplicate-prompt">
          <DialogHeader>
            <DialogTitle>Duplicate to TT pipeline?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground mb-4">
            The follow up is saved. We can copy this patient into Titration, starting at SS Feedback,
            keeping the same referral details. This case stays in the Sleep Study pipeline.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
            <Button
              variant="outline"
              onClick={() => {
                setShowDuplicatePrompt(false);
                onClose();
              }}
              disabled={saving}
              data-testid="stage-modal-duplicate-no"
            >
              No, stay in Sleep Study
            </Button>
            <Button
              onClick={handleDuplicateToTt}
              disabled={saving}
              className="gap-1.5"
              data-testid="stage-modal-duplicate-yes"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />}
              Yes, copy to TT
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(sendBackPrompt)}
        onOpenChange={(open) => {
          if (!open) {
            setSendBackPrompt(null);
            onClose();
          }
        }}
      >
        <DialogContent className="max-w-sm" data-testid="stage-modal-send-back-prompt">
          <DialogHeader>
            <DialogTitle>Send back to {sendBackPrompt?.stage} for rescheduling?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground mb-4">
            {sendBackPrompt?.reason ? `Reason: ${sendBackPrompt.reason}. ` : ''}
            The stage is saved. Sending the case back opens a fresh scheduling attempt; leaving it
            here keeps it in {stageConfig.stageName}.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
            <Button
              variant="outline"
              onClick={() => {
                setSendBackPrompt(null);
                onClose();
              }}
              disabled={saving}
              data-testid="stage-modal-send-back-no"
            >
              No, keep in stage
            </Button>
            <Button
              onClick={handleSendBack}
              disabled={saving}
              className="gap-1.5"
              data-testid="stage-modal-send-back-yes"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Yes, send back
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showCancellationConfirm} onOpenChange={dismissCancellation}>
        <DialogContent className="max-w-sm" data-testid="stage-modal-cancel-confirm">
          <DialogHeader>
            <DialogTitle>Move to Lost/Cancelled?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground mb-4">
            {cancellationField && notEmpty(formData[cancellationField])
              ? `Reason: ${formData[cancellationField]}. `
              : ''}
            {cancelAfterSave
              ? 'The stage is saved. Moving the case marks it Lost/Cancelled — reps can still move it back for re-engagement.'
              : 'Selecting this reason will move the case to Lost/Cancelled. Reps can still move it back for re-engagement.'}
          </p>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={dismissCancellation} data-testid="stage-modal-cancel-no">
              {cancelAfterSave ? 'No, keep in stage' : 'Keep in stage'}
            </Button>
            <Button
              onClick={confirmMoveToCancelled}
              className="bg-red-600 hover:bg-red-700"
              disabled={saving}
              data-testid="stage-modal-cancel-yes"
            >
              {saving ? 'Moving...' : 'Move to Lost/Cancelled'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default StageModal;
